/**
 * InsideLiveDeal — landing page signature showcase.
 * Three-column operational mockup of a live ChainFlow Deal Room.
 * Matches the approved reference: 27 High Street · Hampstead.
 */
import React from "react";
import {
  Home, CheckCircle2, FileText, Hourglass, AlertTriangle, UserCheck,
  Link2, Sparkles, ArrowRight, MoreHorizontal, Clock, Calendar,
  PoundSterling, ChevronRight, Building2, TrendingUp, Activity,
} from "lucide-react";

const PROPERTY_IMG = "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=600&q=70&auto=format&fit=crop";

/* ─── Activity rail (left column) ─────────────────────────────── */
const ACTIVITY = [
  { id: "a1", Icon: Home,          tone: "violet",  title: "Mortgage offer received",   sub: "By Alex Carter · Mortgage Broker", t: "2m ago",  pulse: true },
  { id: "a2", Icon: CheckCircle2,  tone: "emerald", title: "Searches returned",         sub: "By Dawson Solicitors",            t: "18m ago" },
  { id: "a3", Icon: Hourglass,     tone: "amber",   title: "Awaiting seller solicitor", sub: "Management pack",                 t: "1h ago",  badge: "1h ago" },
  { id: "a4", Icon: FileText,      tone: "violet",  title: "Contracts uploaded",        sub: "By Parker & Co",                  t: "2h ago" },
  { id: "a5", Icon: CheckCircle2,  tone: "emerald", title: "Seller signed TA6",         sub: "By James Wilson",                 t: "3h ago" },
  { id: "a6", Icon: AlertTriangle, tone: "amber",   title: "Valuation booked",          sub: "Tuesday 20th May",                t: "4h ago" },
  { id: "a7", Icon: UserCheck,     tone: "violet",  title: "Buyer ID verified",         sub: "By Emma Davies",                  t: "5h ago" },
  { id: "a8", Icon: Link2,         tone: "emerald", title: "Chain complete",            sub: "All parties confirmed",           t: "1d ago" },
];

const TONE_TILE = {
  violet:  { bg: "linear-gradient(135deg, #EDE9FE 0%, #E0E7FF 100%)", fg: "#6D28D9" },
  emerald: { bg: "#ECFDF5", fg: "#059669" },
  amber:   { bg: "#FFFBEB", fg: "#D97706" },
};

const TransactionActivity = () => (
  <aside
    className="rounded-2xl bg-white p-5 hidden lg:flex flex-col"
    style={{ border: "1px solid #E2E8F0", boxShadow: "0 4px 24px -12px rgba(15,23,42,0.10)" }}
    data-testid="ild-activity"
  >
    <div className="flex items-center justify-between mb-5">
      <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-slate-500">Transaction Activity</p>
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600">
        <span className="relative flex w-1.5 h-1.5">
          <span className="absolute inset-0 rounded-full bg-emerald-400 cf-pulse" />
          <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-500" />
        </span>
        Live
      </span>
    </div>
    <ul className="relative flex-1">
      <span aria-hidden="true" className="absolute left-[18px] top-3 bottom-3 w-px bg-gradient-to-b from-slate-200 via-slate-100 to-transparent" />
      {ACTIVITY.map((a, i) => {
        const t = TONE_TILE[a.tone];
        return (
          <li key={a.id} className="relative flex items-start gap-3 py-2.5">
            <span
              className="relative z-[1] w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: t.bg, color: t.fg }}
            >
              <a.Icon size={15} strokeWidth={1.8} />
              {a.pulse && (
                <span className="absolute inset-0 rounded-xl ring-2 ring-violet-300/40 animate-pulse" />
              )}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[12.5px] font-bold text-slate-900 leading-tight truncate">{a.title}</p>
              <p className="text-[10.5px] text-slate-500 mt-0.5 truncate">{a.sub}</p>
              {a.badge && (
                <p className="text-[10px] mt-1 inline-flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-amber-500" />
                  <span className="text-amber-600 font-semibold">{a.badge}</span>
                </p>
              )}
            </div>
            <span className="shrink-0 text-[10px] font-semibold text-slate-400 tabular-nums whitespace-nowrap mt-1">{a.t}</span>
          </li>
        );
      })}
    </ul>
    <button
      type="button"
      className="mt-4 h-10 rounded-xl text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 transition-all hover:-translate-y-0.5"
      style={{
        background: "linear-gradient(180deg, #FFFFFF 0%, #FAFAFE 100%)",
        border: "1px solid #E2E8F0",
        color: "#4F46E5",
        boxShadow: "0 1px 0 rgba(255,255,255,0.9) inset, 0 4px 12px -6px rgba(79,70,229,0.18)",
      }}
    >
      View full activity <ArrowRight size={12} />
    </button>
  </aside>
);

/* ─── Property header + timeline (centre column) ──────────────── */
const PROGRESS = 72;
const STAGES = [
  { id: "s1", label: "Offer Accepted", date: "22 Apr", state: "done" },
  { id: "s2", label: "AML Checks",     date: "24 Apr", state: "done" },
  { id: "s3", label: "Mortgage",       date: "2 May",  state: "done" },
  { id: "s4", label: "Searches",       date: "IN PROGRESS", state: "active" },
  { id: "s5", label: "Enquiries",      date: "AWAITING",    state: "awaiting" },
  { id: "s6", label: "Exchange",       date: "UPCOMING",    state: "upcoming" },
  { id: "s7", label: "Completion",     date: "Est. Aug 2025", state: "upcoming" },
];

const DealHeader = () => {
  const r = 30;
  const c = 2 * Math.PI * r;
  const dash = (PROGRESS / 100) * c;
  return (
    <div
      className="rounded-2xl bg-white p-5"
      style={{ border: "1px solid #E2E8F0", boxShadow: "0 6px 30px -16px rgba(15,23,42,0.10)" }}
    >
      <div className="flex justify-end -mb-1">
        <button className="w-7 h-7 rounded-lg hover:bg-slate-50 inline-flex items-center justify-center text-slate-400">
          <MoreHorizontal size={16} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[176px_1fr_auto] gap-5 items-center -mt-2">
        <div className="relative w-full md:w-[176px] h-[140px] rounded-xl overflow-hidden ring-1 ring-slate-200">
          <img src={PROPERTY_IMG} alt="27 High Street" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent" />
          <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-white/95 backdrop-blur text-[10.5px] font-bold text-slate-900">£1,250,000</span>
        </div>
        <div className="min-w-0">
          <h3 className="font-display text-[26px] md:text-[30px] font-bold text-slate-900 leading-tight tracking-tight">27 High Street</h3>
          <p className="text-[13px] text-slate-500 mt-0.5">Hampstead, London NW3 1QJ</p>
          <div className="grid grid-cols-3 gap-2 mt-4">
            <Pill icon={PoundSterling} label="£1,250,000" sub="Sale price" />
            <Pill icon={Calendar}      label="12 Jun 2025" sub="Target exchange" />
            <Pill icon={Calendar}      label="Aug 2025"    sub="Est. completion" />
          </div>
        </div>
        <div className="relative w-[96px] h-[96px] shrink-0 mx-auto">
          <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
            <circle cx="40" cy="40" r={r} stroke="#F1F5F9" strokeWidth="6" fill="none" />
            <defs>
              <linearGradient id="ild-prog" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%"  stopColor="#8B5CF6" />
                <stop offset="100%" stopColor="#6366F1" />
              </linearGradient>
            </defs>
            <circle cx="40" cy="40" r={r} stroke="url(#ild-prog)" strokeWidth="6" fill="none"
              strokeLinecap="round" strokeDasharray={`${dash} ${c}`} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-display text-[22px] font-bold text-slate-900 leading-none">{PROGRESS}<span className="text-[12px]">%</span></span>
            <span className="text-[9.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold mt-0.5">Progress</span>
          </div>
        </div>
      </div>
      <Timeline />
    </div>
  );
};

const Pill = ({ icon: Icon, label, sub }) => (
  <div className="rounded-xl p-2.5 flex items-center gap-2.5" style={{ border: "1px solid #E2E8F0", background: "#FAFAFE" }}>
    <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#EEF2FF", color: "#6366F1" }}>
      <Icon size={13} />
    </span>
    <div className="min-w-0">
      <p className="text-[12.5px] font-bold text-slate-900 leading-tight truncate">{label}</p>
      <p className="text-[10px] text-slate-500 truncate">{sub}</p>
    </div>
  </div>
);

const Timeline = () => (
  <div className="mt-6 -mx-1 overflow-x-auto cf-scroll">
    <div className="relative min-w-[680px] py-5">
      <span aria-hidden="true" className="absolute top-[26px] left-7 right-7 h-[2px]" style={{ background: "#F1F5F9" }} />
      <span aria-hidden="true" className="absolute top-[26px] left-7 h-[2px]" style={{ width: "calc((100% - 56px) * 0.5)", background: "linear-gradient(90deg, #10B981 0%, #6366F1 100%)" }} />
      <div className="grid grid-cols-7">
        {STAGES.map((s) => {
          const done = s.state === "done";
          const active = s.state === "active";
          const awaiting = s.state === "awaiting";
          return (
            <div key={s.id} className="flex flex-col items-center gap-2 px-1 text-center">
              <span className="relative w-[52px] h-[52px] rounded-full flex items-center justify-center"
                style={{
                  background: done ? "linear-gradient(135deg, #34D399 0%, #10B981 100%)"
                          : active ? "#FFFFFF"
                          : awaiting ? "#FFFFFF"
                          : "#FFFFFF",
                  border: done ? "none" : `2px solid ${active ? "#6366F1" : "#E2E8F0"}`,
                  boxShadow: active ? "0 0 0 4px rgba(99,102,241,0.18), 0 6px 18px -6px rgba(99,102,241,0.45)" : (done ? "0 4px 14px -4px rgba(16,185,129,0.40)" : "none"),
                }}
              >
                {done ? (
                  <CheckCircle2 size={20} className="text-white" strokeWidth={2.4} />
                ) : active ? (
                  <span className="relative inline-flex w-3 h-3">
                    <span className="absolute inset-0 rounded-full bg-indigo-500 cf-pulse" />
                    <span className="relative w-3 h-3 rounded-full bg-indigo-500" />
                  </span>
                ) : (
                  <FileText size={16} className="text-slate-400" />
                )}
              </span>
              <p className="text-[11.5px] font-bold text-slate-900 leading-tight">{s.label}</p>
              {active ? (
                <span className="px-2 h-5 inline-flex items-center rounded-md text-[9px] font-bold tracking-[0.14em] bg-indigo-50 text-indigo-700 border border-indigo-200">{s.date}</span>
              ) : awaiting ? (
                <span className="px-2 h-5 inline-flex items-center rounded-md text-[9px] font-bold tracking-[0.14em] bg-amber-50 text-amber-700 border border-amber-200">{s.date}</span>
              ) : s.state === "upcoming" ? (
                <span className="px-2 h-5 inline-flex items-center rounded-md text-[9px] font-bold tracking-[0.14em] bg-slate-50 text-slate-500 border border-slate-200">{s.date}</span>
              ) : (
                <p className="text-[10.5px] text-slate-500">{s.date}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

/* ─── Participants ────────────────────────────────────────────── */
const PARTICIPANTS = [
  { id: "p1", initials: "ED", name: "Emma Davies",   role: "Buyer",          status: "Active now",      tone: "emerald", img: "https://i.pravatar.cc/80?img=49" },
  { id: "p2", initials: "JW", name: "James Wilson",  role: "Seller",         status: "Active now",      tone: "emerald", img: "https://i.pravatar.cc/80?img=12" },
  { id: "p3", initials: "SM", name: "Sarah Mitchell",role: "Estate Agent",   status: "Active now",      tone: "emerald", img: "https://i.pravatar.cc/80?img=47" },
  { id: "p4", initials: "AC", name: "Alex Carter",   role: "Mortgage Broker",status: "Last reply 1h ago", tone: "amber", img: "https://i.pravatar.cc/80?img=15" },
  { id: "p5", initials: "DS", name: "Dawsons Solicitors", role: "Buyer Solicitor",  status: "Last reply 3h ago", tone: "amber", img: "https://i.pravatar.cc/80?img=44" },
  { id: "p6", initials: "P&C", name: "Parker & Co",      role: "Seller Solicitor", status: "Last reply 2h ago", tone: "amber", img: "https://i.pravatar.cc/80?img=33" },
];

const Participants = () => (
  <section
    className="rounded-2xl bg-white p-5"
    style={{ border: "1px solid #E2E8F0", boxShadow: "0 4px 24px -12px rgba(15,23,42,0.08)" }}
  >
    <div className="flex items-center justify-between mb-4">
      <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-slate-500">Transaction Participants</p>
      <button className="text-[11.5px] font-bold text-indigo-600 hover:text-indigo-700">Manage all</button>
    </div>
    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
      {PARTICIPANTS.map((p) => (
        <div key={p.id} className="text-center">
          <div className="relative w-14 h-14 mx-auto mb-2">
            <img src={p.img} alt={p.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-sm" />
            <span
              className="absolute -bottom-0.5 -right-0.5 w-7 h-7 rounded-full text-white text-[9px] font-bold flex items-center justify-center"
              style={{ background: p.id === "p3" ? "#1E1B4B" : p.id === "p4" ? "#F59E0B" : "#0F172A", border: "2px solid white" }}
            >
              {p.initials}
            </span>
          </div>
          <p className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-slate-500">{p.role}</p>
          <p className="text-[12px] font-bold text-slate-900 leading-tight mt-0.5 truncate">{p.name}</p>
          <p className="text-[10.5px] mt-1 inline-flex items-center gap-1 justify-center">
            <span className={`w-1.5 h-1.5 rounded-full ${p.tone === "emerald" ? "bg-emerald-500" : "bg-amber-500"}`} />
            <span className={p.tone === "emerald" ? "text-emerald-600" : "text-amber-600"}>{p.status}</span>
          </p>
        </div>
      ))}
    </div>
  </section>
);

/* ─── Live Progression Feed ───────────────────────────────────── */
const FEED = [
  { id: "f1", Icon: CheckCircle2, tone: "emerald", title: "Searches returned by buyer solicitor", actor: "Dawsons Solicitors", t: "18m ago" },
  { id: "f2", Icon: Calendar,     tone: "violet",  title: "Mortgage valuation booked for Tuesday 20th May", actor: "Alex Carter", t: "2h ago" },
  { id: "f3", Icon: FileText,     tone: "emerald", title: "Seller signed TA6 documentation", actor: "James Wilson", t: "5h ago" },
  { id: "f4", Icon: Hourglass,    tone: "amber",   title: "Awaiting management pack from building management", actor: "Parker & Co", t: "1d ago" },
];

const ProgressionFeed = () => (
  <section
    className="rounded-2xl bg-white p-5"
    style={{ border: "1px solid #E2E8F0", boxShadow: "0 4px 24px -12px rgba(15,23,42,0.08)" }}
  >
    <div className="flex items-center justify-between mb-4">
      <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-slate-500">Live Progression Feed</p>
      <button className="text-[11.5px] font-bold text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1">View full timeline <ArrowRight size={11} /></button>
    </div>
    <ul className="space-y-2">
      {FEED.map((f) => {
        const t = TONE_TILE[f.tone];
        return (
          <li key={f.id} className="flex items-center gap-3 py-1.5">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: t.bg, color: t.fg }}>
              <f.Icon size={14} strokeWidth={1.8} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[12.5px] font-bold text-slate-900 leading-tight">{f.title}</p>
            </div>
            <span className="px-2 h-6 inline-flex items-center rounded-md text-[10.5px] font-semibold bg-slate-50 text-slate-600 border border-slate-200 whitespace-nowrap">{f.actor}</span>
            <span className="text-[10.5px] text-slate-400 tabular-nums whitespace-nowrap shrink-0">{f.t}</span>
          </li>
        );
      })}
    </ul>
  </section>
);

/* ─── Flow Intelligence (right column) ────────────────────────── */
const Ring = ({ value, color = "#6366F1", size = 44 }) => {
  const r = size / 2 - 4;
  const c = 2 * Math.PI * r;
  const dash = (value / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full -rotate-90">
        <circle cx={size/2} cy={size/2} r={r} stroke="#F1F5F9" strokeWidth="3.5" fill="none" />
        <circle cx={size/2} cy={size/2} r={r} stroke={color} strokeWidth="3.5" fill="none" strokeLinecap="round" strokeDasharray={`${dash} ${c}`} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[11px] font-bold tabular-nums text-slate-900 leading-none flex items-baseline">
          {value}<span className="text-[8px] text-slate-400 ml-0.5">/100</span>
        </span>
      </div>
    </div>
  );
};

const IntelCard = ({ children, className = "" }) => (
  <div className={`rounded-xl bg-white p-4 ${className}`}
    style={{ border: "1px solid #E2E8F0", boxShadow: "0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 12px -6px rgba(15,23,42,0.08)" }}>
    {children}
  </div>
);

/* Tiny inline trend line */
const Trend = ({ down, color }) => (
  <svg width="56" height="20" viewBox="0 0 56 20" className="shrink-0">
    <polyline points={down ? "0,4 14,8 28,5 42,11 56,16" : "0,16 14,12 28,14 42,7 56,3"} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FlowIntelligence = () => (
  <aside className="rounded-2xl bg-white p-5 hidden lg:flex flex-col gap-3"
    style={{ border: "1px solid #E2E8F0", boxShadow: "0 4px 24px -12px rgba(15,23,42,0.10)" }}
    data-testid="ild-flow-intel">
    <div className="flex items-center gap-2 mb-1">
      <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, #EDE9FE 0%, #E0E7FF 100%)", color: "#6D28D9" }}>
        <Sparkles size={13} />
      </span>
      <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-indigo-700">Flow Intelligence</p>
    </div>

    <IntelCard>
      <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">Exchange Readiness</p>
      <div className="flex items-center justify-between mt-2">
        <div>
          <p className="font-display text-[18px] font-bold text-emerald-600 leading-none">Good</p>
          <p className="text-[10.5px] text-slate-500 mt-1">On track for exchange</p>
        </div>
        <Ring value={72} color="#10B981" />
      </div>
    </IntelCard>

    <IntelCard>
      <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">Completion Outlook</p>
      <div className="flex items-center justify-between mt-2">
        <div>
          <p className="font-display text-[18px] font-bold text-emerald-600 leading-none">High</p>
          <p className="text-[10.5px] text-slate-500 mt-1">On track for Aug 2025</p>
        </div>
        <Ring value={85} color="#10B981" />
      </div>
    </IntelCard>

    <div className="grid grid-cols-2 gap-3">
      <IntelCard>
        <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">Revenue At Risk</p>
        <p className="font-display text-[18px] font-bold text-rose-600 mt-2 leading-none">£125,000</p>
        <p className="text-[10.5px] text-rose-500 mt-1">Medium risk</p>
        <Trend down color="#F43F5E" />
      </IntelCard>
      <IntelCard>
        <div className="flex items-start justify-between">
          <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">Chain Health</p>
          <Link2 size={11} className="text-slate-400" />
        </div>
        <p className="font-display text-[18px] font-bold text-emerald-600 mt-2 leading-none">Strong</p>
        <p className="text-[10.5px] text-slate-500 mt-1">All parties aligned</p>
      </IntelCard>
    </div>

    <div className="grid grid-cols-2 gap-3">
      <IntelCard>
        <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">Deal Momentum</p>
        <p className="font-display text-[16px] font-bold text-emerald-600 mt-2 leading-none">Improving</p>
        <p className="text-[10.5px] text-emerald-600 font-semibold mt-1 inline-flex items-center gap-1"><TrendingUp size={10} />18% this week</p>
      </IntelCard>
      <IntelCard>
        <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">Stakeholder Responsiveness</p>
        <p className="font-display text-[16px] font-bold text-emerald-600 mt-2 leading-none">Very Good</p>
        <p className="text-[10.5px] text-slate-500 mt-1">Avg. reply time: 2.3h</p>
      </IntelCard>
    </div>

    <div className="grid grid-cols-2 gap-3">
      <IntelCard>
        <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">Awaiting Actions</p>
        <p className="font-display text-[24px] font-bold text-amber-600 mt-1.5 leading-none">3</p>
        <p className="text-[10.5px] text-amber-600 mt-0.5">Requires attention</p>
      </IntelCard>
      <IntelCard>
        <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">Risk Trend</p>
        <p className="font-display text-[16px] font-bold text-slate-900 mt-2 leading-none">Stable</p>
        <p className="text-[10.5px] text-slate-500 mt-1">No change</p>
      </IntelCard>
    </div>

    <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 mt-1">Estimated Windows</p>
    <div className="grid grid-cols-2 gap-3">
      <IntelCard>
        <div className="flex items-start justify-between">
          <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">Exchange Window</p>
          <Calendar size={11} className="text-slate-400" />
        </div>
        <p className="font-display text-[16px] font-bold text-slate-900 mt-2 leading-none">10 – 14 Jun</p>
        <p className="text-[10.5px] text-slate-500 mt-1">5 days</p>
      </IntelCard>
      <IntelCard>
        <div className="flex items-start justify-between">
          <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">Completion Window</p>
          <Calendar size={11} className="text-slate-400" />
        </div>
        <p className="font-display text-[16px] font-bold text-slate-900 mt-2 leading-none">Aug 2025</p>
        <p className="text-[10.5px] text-emerald-600 mt-1 font-semibold">On track</p>
      </IntelCard>
    </div>
  </aside>
);

/* ─── Trust pills ─────────────────────────────────────────────── */
const TRUST_PILLS = [
  "Built for UK estate agencies",
  "Reduce transaction fall-throughs",
  "Real-time deal progression",
  "Buyers, sellers & solicitors aligned",
  "Faster progression to exchange",
  "Replace inbox & WhatsApp chaos",
  "Every deal in one operational workspace",
  "Built from 15+ years inside UK estate agencies",
];

const TrustPill = ({ label, alt }) => (
  <span
    className="inline-flex items-center gap-2 h-9 px-3.5 rounded-full text-[12px] font-semibold text-slate-700 transition-all hover:-translate-y-0.5"
    style={{
      background: "rgba(255,255,255,0.7)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      border: "1px solid rgba(196,181,253,0.45)",
      boxShadow: "0 4px 14px -8px rgba(99,102,241,0.18)",
    }}
  >
    {alt ? (
      <span className="w-3.5 h-3.5 rounded-full bg-violet-100 inline-flex items-center justify-center">
        <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
      </span>
    ) : (
      <CheckCircle2 size={13} className="text-violet-600" strokeWidth={2.4} />
    )}
    {label}
  </span>
);

/* ─── Main showcase ────────────────────────────────────────────── */
export const InsideLiveDeal = () => (
  <section
    className="relative overflow-hidden py-20 lg:py-28"
    data-testid="inside-live-deal"
    style={{
      background: "radial-gradient(ellipse at 50% 0%, rgba(196,181,253,0.20) 0%, rgba(238,242,255,0.50) 35%, #FAFAFE 70%, #FFFFFF 100%)",
    }}
  >
    {/* Atmospheric glows */}
    <div aria-hidden="true" className="absolute inset-0 -z-10 pointer-events-none">
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full blur-3xl opacity-50"
        style={{ background: "radial-gradient(circle, rgba(167,139,250,0.18), transparent 70%)" }} />
      <div className="absolute -top-24 -right-32 w-[420px] h-[420px] rounded-full blur-3xl opacity-50"
        style={{ background: "radial-gradient(circle, rgba(99,102,241,0.15), transparent 70%)" }} />
    </div>

    <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
      {/* INSIDE A LIVE DEAL pill */}
      <div className="flex justify-center mb-6 cf-fade-up">
        <span
          className="inline-flex items-center gap-2 h-8 px-3.5 rounded-full text-[10.5px] font-bold uppercase tracking-[0.22em]"
          style={{
            background: "rgba(255,255,255,0.85)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            border: "1px solid rgba(196,181,253,0.55)",
            color: "#6D28D9",
            boxShadow: "0 6px 18px -8px rgba(139,92,246,0.30)",
          }}
        >
          Inside A Live Deal
          <span className="relative flex w-1.5 h-1.5">
            <span className="absolute inset-0 rounded-full bg-violet-400 cf-pulse" />
            <span className="relative w-1.5 h-1.5 rounded-full bg-violet-500" />
          </span>
        </span>
      </div>

      {/* Headline */}
      <h2
        className="font-display text-center font-bold tracking-[-0.04em] text-slate-900 leading-[1.0] cf-fade-up"
        style={{ fontSize: "clamp(36px, 6.6vw, 84px)", animationDelay: "60ms" }}
      >
        The operating system for
        <br />
        <span style={{ background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 50%, #4338CA 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          modern property transactions
        </span>
      </h2>

      {/* Subhead */}
      <p
        className="mt-6 text-center font-medium text-slate-600 max-w-[820px] mx-auto leading-relaxed cf-fade-up"
        style={{ fontSize: "clamp(15px, 1.4vw, 18px)", animationDelay: "140ms" }}
      >
        Replace fragmented communication, unclear progression and transaction delays with one
        live operational workspace built for estate agencies.
      </p>

      {/* Trust pills */}
      <div className="mt-9 flex items-center justify-center gap-x-2.5 gap-y-3 flex-wrap max-w-[1200px] mx-auto cf-fade-up" style={{ animationDelay: "220ms" }}>
        {TRUST_PILLS.map((label, i) => <TrustPill key={label} label={label} alt={i % 2 === 1} />)}
      </div>

      {/* 3-column showcase */}
      <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-[300px_1fr_320px] gap-5 cf-fade-up" style={{ animationDelay: "320ms" }}>
        <TransactionActivity />
        <div className="flex flex-col gap-5">
          <DealHeader />
          <Participants />
          <ProgressionFeed />
        </div>
        <FlowIntelligence />
      </div>
    </div>
  </section>
);

export default InsideLiveDeal;
